import type { Momentum } from '../lib/types';
import { label } from '../lib/format';

/**
 * The six axes a momentum score is made of, and the events that fed them.
 *
 * `AGENTS.md` forbids an opaque aggregate ranking and asks that the components
 * and the evidence stay visible; `docs/architecture.md` makes decomposability a
 * design principle. Both were honoured on the wire and nowhere else — the
 * dimensions and the per-event contributions had been computed and served since
 * the first release while the dashboard showed a bare number and a bar, which is
 * precisely the shape those rules exist to rule out.
 *
 * Behind a `<details>` because the leaderboard is a ranking first: ten companies
 * each unfolding six axes and their events is a wall, not a disclosure. Native
 * rather than React state so it is keyboard reachable and needs no controller.
 */
const DIMENSIONS = [
  'scientific',
  'experimental',
  'commercial',
  'organizational',
  'capital',
  'evidenceQuality',
] as const;

export function MomentumBreakdown({ score }: { score: Momentum }) {
  // Sorted by weight so the reason a company ranks where it does is the first
  // thing read, and truncated because a long-lived company accumulates more
  // contributions than a summary can carry. The count says what was left out;
  // a silently short list would be the same failure the totals elsewhere fix.
  const contributions = [...score.components].sort((a, b) => b.points - a.points);
  const shown = contributions.slice(0, 5);
  return (
    <details className="breakdown">
      <summary>Why this score</summary>
      <dl className="breakdown__axes">
        {DIMENSIONS.map((dimension) => (
          <div key={dimension}>
            <dt>{label(dimension === 'evidenceQuality' ? 'evidence_quality' : dimension)}</dt>
            <dd>{score[dimension]}</dd>
          </div>
        ))}
      </dl>
      {shown.length > 0 && (
        <ul className="breakdown__events">
          {shown.map((contribution) => (
            <li key={contribution.eventId}>
              <span>{contribution.eventTitle || 'Untitled event'}</span>
              <small className="muted">{label(contribution.dimension)}</small>
              <b>+{contribution.points}</b>
            </li>
          ))}
        </ul>
      )}
      {contributions.length > shown.length && (
        <small className="muted">
          Showing the {shown.length} largest of {contributions.length} contributions.
        </small>
      )}
    </details>
  );
}
